import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, AnimatePresence } from "framer-motion";
import {
  Target,
  Eye,
  Award,
  Users,
  Building2,
  CheckCircle2,
  Star,
  Send,
  Loader2,
  Quote,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useCMS } from "@/context/CMSContext";

function AnimatedSection({
  children,
  className = "",
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.6, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function PageHeader() {
  return (
    <section className="pt-28 pb-12 bg-gradient-to-br from-primary via-primary to-primary-light relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute -top-20 -right-20 w-64 h-64 rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(219,106,64,0.25) 0%, transparent 70%)",
          }}
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.5, 0.8, 0.5],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </div>

      <div className="section-padding max-w-7xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-left"
        >
          <motion.nav
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="flex items-center gap-2 text-sm mb-4"
          >
            <Link
              to="/"
              className="text-white/60 hover:text-accent transition-colors"
            >
              Home
            </Link>
            <span className="text-white/40">/</span>
            <span className="text-accent font-medium">About</span>
          </motion.nav>

          <motion.span
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 backdrop-blur-sm rounded-full text-accent font-medium text-xs mb-4"
          >
            <span className="w-1.5 h-1.5 bg-accent rounded-full animate-pulse" />
            Who We Are
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="text-3xl md:text-4xl font-bold text-white mb-3"
          >
            About Bomtrack
          </motion.h1>

          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="w-16 h-0.5 bg-accent origin-left mb-4 rounded-full"
          />

          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="text-white/70 max-w-xl text-base"
          >
            Learn about our journey, mission, and commitment to excellence in
            land surveying.
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
}

function CompanyStory() {
  return (
    <section className="py-16 bg-white">
      <div className="section-padding max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <AnimatedSection>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=800&auto=format&fit=crop&q=80"
                  alt="Our Office"
                  className="w-full h-[450px] object-cover"
                />
              </div>
              <div className="absolute -bottom-5 -right-5 bg-accent text-white rounded-xl p-5 shadow-xl">
                <div className="text-3xl font-bold">2018</div>
                <div className="text-xs opacity-90">Established</div>
              </div>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={0.2}>
            <div className="flex items-center gap-2 mb-4">
              <span className="w-8 h-0.5 bg-accent rounded-full" />
              <span className="text-accent font-medium text-sm uppercase tracking-wide">
                Our Story
              </span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-primary mb-5">
              Building Trust Through Precision Since 2018
            </h2>
            <div className="space-y-4 text-neutral-600 text-sm leading-relaxed">
              <p>
                BOMTRACK Nigeria Limited was established in 2018 by experienced
                and seasoned surveyors, geodesist and digital mappers.
              </p>
              <p>
                Over the years, our team of experts have built expert operations
                beyond geodesy and surveying into Geomatics through the
                acquisition of Geographic Information Technology.
              </p>
              <p>
                Our team of 19 employees has been able to achieve success
                through our ability to understand the specific requirements of
                each client coupled with our unrivalled level of service and
                workmanship.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-8">
              {[
                { value: "300+", label: "Projects Completed" },
                { value: "08+", label: "Expert Surveyors" },
                { value: "24", label: "States Covered" },
                { value: "08+", label: "Years Active" },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="bg-neutral-50 rounded-xl p-4 border border-neutral-100"
                >
                  <div className="text-xl font-bold text-primary mb-1">
                    {stat.value}
                  </div>
                  <div className="text-xs text-neutral-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
}

function MissionVision() {
  return (
    <section className="py-16 bg-neutral-50">
      <div className="section-padding max-w-7xl mx-auto">
        <AnimatedSection className="text-center mb-10">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-accent/10 rounded-full text-accent font-medium text-sm mb-4">
            <Target className="w-4 h-4" />
            Our Purpose
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-primary">
            Mission & Vision
          </h2>
        </AnimatedSection>

        <div className="grid md:grid-cols-2 gap-6">
          <AnimatedSection>
            <div className="bg-white rounded-2xl p-6 border border-neutral-100 h-full hover:shadow-lg hover:shadow-accent/5 hover:border-accent/20 transition-all duration-300">
              <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center mb-5">
                <Target className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-primary mb-3">
                Our Mission
              </h3>
              <p className="text-neutral-600 text-sm leading-relaxed">
                To provide exceptional land surveying services that exceed
                client expectations through the application of advanced
                technology, professional expertise, and unwavering commitment to
                accuracy. We aim to be the trusted partner for all land-related
                needs in Nigeria.
              </p>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={0.1}>
            <div className="bg-white rounded-2xl p-6 border border-neutral-100 h-full hover:shadow-lg hover:shadow-accent/5 hover:border-accent/20 transition-all duration-300">
              <div className="w-12 h-12 bg-accent rounded-xl flex items-center justify-center mb-5">
                <Eye className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-primary mb-3">
                Our Vision
              </h3>
              <p className="text-neutral-600 text-sm leading-relaxed">
                To be the leading land surveying company in Nigeria, recognized
                for excellence, innovation, and reliability. We envision a
                future where every land transaction and development project in
                Nigeria is supported by accurate, timely, and professional
                surveying services.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
}

function CoreValues() {
  const values = [
    {
      icon: Award,
      title: "Excellence",
      description:
        "We strive for excellence in every project, ensuring the highest standards of accuracy and professionalism.",
    },
    {
      icon: CheckCircle2,
      title: "Integrity",
      description:
        "We conduct our business with honesty and transparency, building trust with clients and stakeholders.",
    },
    {
      icon: Users,
      title: "Client Focus",
      description:
        "We put our clients first, understanding their needs and delivering solutions that exceed expectations.",
    },
    {
      icon: Building2,
      title: "Innovation",
      description:
        "We embrace new technologies and methodologies to improve our services and efficiency.",
    },
  ];

  return (
    <section className="py-16 bg-white">
      <div className="section-padding max-w-7xl mx-auto">
        <AnimatedSection className="text-center mb-10">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-accent/10 rounded-full text-accent font-medium text-sm mb-4">
            <Star className="w-4 h-4" />
            Our Values
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-primary mb-3">
            What We Stand For
          </h2>
          <p className="text-neutral-500 text-sm max-w-xl mx-auto">
            Our core values guide everything we do, from how we work with
            clients to how we treat our team members.
          </p>
        </AnimatedSection>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {values.map((value, index) => (
            <AnimatedSection key={value.title} delay={index * 0.1}>
              <div className="bg-neutral-50 rounded-2xl p-6 border border-neutral-100 hover:border-accent/20 hover:shadow-lg hover:shadow-accent/5 transition-all duration-300 h-full">
                <div className="w-11 h-11 bg-primary rounded-xl flex items-center justify-center mb-4">
                  <value.icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-base font-semibold text-primary mb-2">
                  {value.title}
                </h3>
                <p className="text-neutral-500 text-sm leading-relaxed">
                  {value.description}
                </p>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
}

function TeamSection() {
  const team = [
    {
      name: "Surv. Bamgbose O. Micheal",
      role: "CEO/ Managing Director",
      image: "/team/micheal.jpg",
    },
    {
      name: "Ajeigbe Joseph O.",
      role: "Director of Operations",
      image: "/team/joseph.jpg",
    },
    {
      name: "Oyebode Samuel D.",
      role: "Administrative Coordinator",
      image: "/team/samuel.jpg",
    },
    {
      name: "Fodeso Adegoke M.",
      role: "Party Chief",
      image: "/team/adegoke.jpg",
    },
    {
      name: "Lateef Morenikeji C.",
      role: "Geomatic Technician",
      image: "/team/keji.jpg",
    },
    {
      name: "Ejiudunaka Rosemary V.",
      role: "Secretary",
      image: "/team/rose.jpg",
    },
  ];

  return (
    <section className="py-16 bg-neutral-50">
      <div className="section-padding max-w-7xl mx-auto">
        <AnimatedSection className="text-center mb-10">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-accent/10 rounded-full text-accent font-medium text-sm mb-4">
            <Users className="w-4 h-4" />
            Our Team
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-primary mb-3">
            Meet Our Experts
          </h2>
          <p className="text-neutral-500 text-sm max-w-xl mx-auto">
            Our team of licensed professionals brings decades of combined
            experience to every project.
          </p>
        </AnimatedSection>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {team.map((member, index) => (
            <AnimatedSection key={member.name} delay={index * 0.1}>
              <div className="bg-white rounded-2xl overflow-hidden border border-neutral-100 shadow-sm group hover:shadow-xl hover:shadow-accent/5 hover:border-accent/20 transition-all duration-300">
                <div className="relative aspect-[5/6] overflow-hidden">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
                <div className="p-5">
                  <h3 className="text-base font-semibold text-primary mb-1">
                    {member.name}
                  </h3>
                  <p className="text-accent text-sm">{member.role}</p>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
}

function Certifications() {
  const certs = [
    {
      text: "SURCON (Surveyors Council of Nigeria)",
      image: "/imgs/surcon.jpg",
    },    
    {
      text: "Member of Nigerian Institution of Surveyors",
      image: "/imgs/nis.jpg",
    },
    { text: "Association of Private Practicing Surveyors of Nigeria", 
      image: "/imgs/appsn.jpg" },
    // { text: "ISO 9001:2015 Quality Management", image: "/imgs/iso.jpg" },
  ];

  return (
    <section className="py-16 bg-primary text-white relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.2) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.2) 1px, transparent 1px)`,
          backgroundSize: "40px 40px",
        }}
      />

      <div className="section-padding max-w-7xl mx-auto relative z-10">
        <AnimatedSection className="text-center mb-10">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/10 backdrop-blur-sm rounded-full text-accent font-medium text-sm mb-4">
            <Award className="w-4 h-4" />
            Certifications
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
            Licensed & Certified
          </h2>
          <p className="text-white/60 text-sm max-w-md mx-auto">
            We meet all regulatory requirements and maintain the highest
            professional standards.
          </p>
        </AnimatedSection>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-5xl mx-auto">
          {certs.map((cert, index) => (
            <AnimatedSection key={cert.text} delay={index * 0.1}>
              <div className="flex items-center gap-4 bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-300">
                <img
                  src={cert.image}
                  alt={cert.text}
                  className="w-16 h-16 rounded-lg object-cover"
                />
                <p className="text-sm text-white/90 leading-relaxed">{cert.text}</p>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Reviews Section ───

function StarDisplay({
  rating,
  size = "sm",
}: {
  rating: number;
  size?: "sm" | "md" | "lg";
}) {
  const sizeClass =
    size === "lg" ? "w-6 h-6" : size === "md" ? "w-5 h-5" : "w-4 h-4";
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`${sizeClass} ${
            star <= rating
              ? "text-yellow-400 fill-yellow-400"
              : "text-neutral-300"
          }`}
        />
      ))}
    </div>
  );
}

function StarRatingInput({
  rating,
  onChange,
}: {
  rating: number;
  onChange: (rating: number) => void;
}) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          onMouseEnter={() => setHover(star)}
          onMouseLeave={() => setHover(0)}
          className="focus:outline-none transition-transform hover:scale-110"
        >
          <Star
            className={`w-7 h-7 transition-colors ${
              star <= (hover || rating)
                ? "text-yellow-400 fill-yellow-400"
                : "text-neutral-300"
            }`}
          />
        </button>
      ))}
    </div>
  );
}

function RatingBar({
  stars,
  count,
  total,
}: {
  stars: number;
  count: number;
  total: number;
}) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="text-sm font-medium text-neutral-700 w-3">{stars}</span>
      <Star className="w-3.5 h-3.5 text-neutral-400 fill-neutral-400" />
      <div className="flex-1 h-2 bg-neutral-100 rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          whileInView={{ width: `${pct}%` }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="h-full bg-accent rounded-full"
        />
      </div>
      <span className="text-xs text-neutral-400 w-9 text-right">{pct}%</span>
    </div>
  );
}

function ReviewsAndForm() {
  const { reviews, isLoading, addReview } = useCMS();
  const [activeSlide, setActiveSlide] = useState(0);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    rating: 0,
    comment: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  // Show 4 reviews per slide, stacked vertically in a single column
  const reviewsPerSlide = 4;

  // Auto-slide carousel
  useEffect(() => {
    const totalSlides = Math.ceil(reviews.length / reviewsPerSlide);
    if (totalSlides <= 1) return;
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % totalSlides);
    }, 6000);
    return () => clearInterval(interval);
  }, [reviews.length, reviewsPerSlide]);

  const avgRating =
    reviews.length > 0
      ? (
          reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length
        ).toFixed(1)
      : "0.0";

  const ratingCounts = [5, 4, 3, 2, 1].map((s) => ({
    stars: s,
    count: reviews.filter((r) => r.rating === s).length,
  }));

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString("en-NG", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    try {
      const result = await addReview(formData);
      setSubmitStatus({
        type: "success",
        message: result.message || "Thank you!",
      });
      setFormData({ name: "", email: "", rating: 0, comment: "" });
    } catch (error) {
      setSubmitStatus({
        type: "error",
        message: error instanceof Error ? error.message : "Failed to submit",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalSlides = Math.ceil(reviews.length / reviewsPerSlide);
  const visibleReviews = reviews.slice(
    activeSlide * reviewsPerSlide,
    activeSlide * reviewsPerSlide + reviewsPerSlide,
  );

  const nextSlide = () => setActiveSlide((prev) => (prev + 1) % totalSlides);
  const prevSlide = () =>
    setActiveSlide((prev) => (prev - 1 + totalSlides) % totalSlides);

  return (
    <section id="reviews" className="py-16 lg:py-20 bg-neutral-50">
      <div className="section-padding max-w-7xl mx-auto">
        <AnimatedSection className="mb-10">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-8 h-0.5 bg-accent rounded-full" />
            <span className="text-accent text-xs font-medium uppercase tracking-wide">
              Testimonials
            </span>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-primary">
            Client Reviews
          </h2>
        </AnimatedSection>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 text-accent animate-spin" />
          </div>
        ) : reviews.length === 0 ? (
          <AnimatedSection className="text-center py-12">
            <Quote className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
            <p className="text-neutral-500">
              No reviews yet. Be the first to share your experience!
            </p>
          </AnimatedSection>
        ) : (
          <div className="grid lg:grid-cols-5 gap-8 lg:gap-10">
            {/* LEFT: Rating Summary + Form (40%) */}
            <div className="lg:col-span-2 space-y-8">
              {/* Average Rating Card */}
              <AnimatedSection>
                <div className="bg-white rounded-2xl p-6 border border-neutral-100">
                  <h3 className="text-lg font-semibold text-primary mb-5">
                    Average Rating
                  </h3>
                  <div className="flex items-end gap-3 mb-6">
                    <span className="text-5xl font-bold text-primary">
                      {avgRating}
                    </span>
                    <div className="mb-1.5">
                      <StarDisplay
                        rating={Math.round(parseFloat(avgRating))}
                        size="md"
                      />
                      <p className="text-sm text-neutral-400 mt-1">
                        {reviews.length} Reviews
                      </p>
                      <div className="flex gap-2 mt-1">
                        <span className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded border border-blue-100">
                          {reviews.filter((r) => r.source === "google").length}{" "}
                          Google
                        </span>
                        <span className="text-[10px] px-1.5 py-0.5 bg-purple-50 text-purple-600 rounded border border-purple-100">
                          {reviews.filter((r) => r.source === "website").length}{" "}
                          Website
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2.5">
                    {ratingCounts.map((rc) => (
                      <RatingBar
                        key={rc.stars}
                        stars={rc.stars}
                        count={rc.count}
                        total={reviews.length}
                      />
                    ))}
                  </div>
                </div>
              </AnimatedSection>

              {/* Add Review Form */}
              <AnimatedSection delay={0.1}>
                <div className="bg-white rounded-2xl p-6 border border-neutral-100">
                  <h3 className="text-lg font-semibold text-primary mb-5">
                    Add a Review
                  </h3>

                  {submitStatus?.type === "success" ? (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="text-center py-6"
                    >
                      <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <CheckCircle2 className="w-7 h-7 text-green-600" />
                      </div>
                      <h4 className="font-semibold text-primary mb-1">
                        Review Submitted!
                      </h4>
                      <p className="text-sm text-neutral-500 mb-4">
                        {submitStatus.message}
                      </p>
                      <button
                        onClick={() => setSubmitStatus(null)}
                        className="px-5 py-2 bg-accent text-white rounded-xl text-sm font-medium hover:bg-accent/90 transition-colors"
                      >
                        Write Another
                      </button>
                    </motion.div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <label className="block text-xs font-medium text-neutral-500 mb-1.5">
                          Add Your Rating{" "}
                          <span className="text-red-400">*</span>
                        </label>
                        <StarRatingInput
                          rating={formData.rating}
                          onChange={(r) =>
                            setFormData((p) => ({ ...p, rating: r }))
                          }
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-neutral-500 mb-1.5">
                          Name <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.name}
                          onChange={(e) =>
                            setFormData((p) => ({ ...p, name: e.target.value }))
                          }
                          placeholder="John Doe"
                          className="w-full px-4 py-2.5 rounded-xl border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none text-sm"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-neutral-500 mb-1.5">
                          Email <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              email: e.target.value,
                            }))
                          }
                          placeholder="john@email.com"
                          className="w-full px-4 py-2.5 rounded-xl border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none text-sm"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-neutral-500 mb-1.5">
                          Write Your Review{" "}
                          <span className="text-red-400">*</span>
                        </label>
                        <textarea
                          required
                          rows={3}
                          value={formData.comment}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              comment: e.target.value,
                            }))
                          }
                          placeholder="Share your experience..."
                          className="w-full px-4 py-2.5 rounded-xl border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none text-sm resize-none"
                        />
                      </div>

                      {submitStatus?.type === "error" && (
                        <p className="text-sm text-red-500">
                          {submitStatus.message}
                        </p>
                      )}

                      <button
                        type="submit"
                        disabled={
                          isSubmitting ||
                          !formData.name ||
                          !formData.email ||
                          formData.rating === 0 ||
                          formData.comment.length < 10
                        }
                        className="w-full py-2.5 bg-accent text-white rounded-xl text-sm font-medium hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {isSubmitting ? "Submitting..." : "Submit"}
                      </button>
                    </form>
                  )}
                </div>
              </AnimatedSection>
            </div>

            {/* RIGHT: Customer Feedback Carousel (60%) */}
            <div className="lg:col-span-3">
              <AnimatedSection delay={0.15}>
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-lg font-semibold text-primary">
                    Customer Feedback
                  </h3>
                  {totalSlides > 1 && (
                    <div className="flex gap-2">
                      <button
                        onClick={prevSlide}
                        className="p-2 rounded-xl border border-neutral-200 hover:bg-white transition-colors"
                      >
                        <ChevronLeft className="w-4 h-4 text-neutral-500" />
                      </button>
                      <button
                        onClick={nextSlide}
                        className="p-2 rounded-xl border border-neutral-200 hover:bg-white transition-colors"
                      >
                        <ChevronRight className="w-4 h-4 text-neutral-500" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Stacked carousel: 1 review per slide */}
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeSlide}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.35 }}
                    className="flex flex-col gap-4"
                  >
                    {visibleReviews.map((review) => (
                      <div
                        key={review.id}
                        className="bg-white rounded-2xl overflow-hidden border border-neutral-100 shadow-sm hover:shadow-lg hover:shadow-accent/5 hover:border-accent/20 transition-all duration-300 flex flex-col"
                      >
                        {/* Card header with colored avatar */}
                        <div className="p-5 flex-1">
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <div
                                className="w-11 h-11 rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0"
                                style={{
                                  backgroundColor: [
                                    "#3b82f6",
                                    "#8b5cf6",
                                    "#ef4444",
                                    "#10b981",
                                    "#f59e0b",
                                  ][(review.id || 0) % 5],
                                }}
                              >
                                {review.name.charAt(0).toUpperCase()}
                              </div>
                              <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                  <p className="text-sm font-semibold text-primary truncate">
                                    {review.name}
                                  </p>
                                  {review.source === "google" && (
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded text-[10px] font-medium border border-blue-100 flex-shrink-0">
                                      <svg
                                        className="w-3 h-3"
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                      >
                                        <path d="M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z" />
                                      </svg>
                                      Google
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-neutral-400">
                                  {formatDate(review.created_at)}
                                </p>
                              </div>
                            </div>
                            <StarDisplay rating={review.rating} size="sm" />
                          </div>
                          <p className="text-sm text-neutral-600 leading-relaxed line-clamp-4">
                            &ldquo;{review.comment}&rdquo;
                          </p>
                        </div>
                      </div>
                    ))}
                  </motion.div>
                </AnimatePresence>

                {/* Dots */}
                {totalSlides > 1 && (
                  <div className="flex justify-center gap-2 mt-5">
                    {Array.from({ length: totalSlides }, (_, i) => (
                      <button
                        key={i}
                        onClick={() => setActiveSlide(i)}
                        className={`h-2 rounded-full transition-all ${
                          i === activeSlide
                            ? "bg-accent w-6"
                            : "bg-neutral-300 w-2 hover:bg-neutral-400"
                        }`}
                      />
                    ))}
                  </div>
                )}
              </AnimatedSection>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

export default function About() {
  return (
    <main className="min-h-screen">
      <PageHeader />
      <CompanyStory />
      <MissionVision />
      <CoreValues />
      <TeamSection />
      <Certifications />
      <ReviewsAndForm />
    </main>
  );
}
